"use client";

import { useTranslation } from "react-i18next";
import { useConfigStore } from "@/store/configStore";
import { useEffect, useRef, useState } from "react";

export default function EnvironmentBanner() {
  const { t } = useTranslation("common");
  const environment = useConfigStore.getState().environment;

  const shouldShow = environment === "local" || environment === "development";

  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);

  // Set initial position to bottom-right once mounted
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({
      x: window.innerWidth - rect.width - 16,
      y: window.innerHeight - rect.height - 16,
    });
  }, []);

  useEffect(() => {
    if (!dragging) return;

    const onMouseMove = (e: MouseEvent) => {
      setPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };

    const onMouseUp = () => setDragging(false);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging]);

  if (!shouldShow) return null;

  const isLocal = environment === "local";

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setDragging(true);
  };

  return (
    <div
      ref={ref}
      onMouseDown={handleMouseDown}
      style={{ left: pos.x, top: pos.y }}
      className={`fixed z-[9999] px-3 py-1 rounded-full text-xs font-semibold text-white shadow-lg select-none ${
        dragging ? "cursor-grabbing" : "cursor-grab"
      } ${isLocal ? "bg-emerald-500" : "bg-amber-400 text-amber-900"}`}
    >
      {isLocal
        ? t("environmentBanner.local")
        : t("environmentBanner.development")}
    </div>
  );
}
